import { useAllUsers } from "http-hooks/users/useUsers";
import { useAllGodowns } from "http-hooks/godown/useGodown";
import DeleteUser from "components/users/DeleteUser";
import SetUser from "components/users/SetUser";
import { useState } from "react";
import Modal from "components/ui/modals";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  LoadingRow,
  EmptyRow,
  ActionButton,
} from "components/ui/Table";
import InputSearch from "components/ui/InputSearch";
import Select from "components/ui/Select";
import Pagination from "components/ui/Pagination";
import {
  PencilSquareIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

// Constants moved to top to prevent re-renders
const statusOptions = [
  { value: "", label: "All Status" },
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
];

const initialFilter = {
  name: "",
  username: "",
  flag: "",
  page: 1,
  limit: 10,
};

export default function CompanyUsers() {
  const navigate = useNavigate();
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [setUserId, setSetUserId] = useState(undefined);
  const [isSetModalOpen, setIsSetModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [filter, setFilter] = useState(initialFilter);

  const openSetModal = (id) => {
    setSetUserId(id);
    setIsSetModalOpen(true);
  };

  const closeSetModal = () => {
    setSetUserId(undefined);
    setIsSetModalOpen(false);
  };

  const handleSearch = (searchTerm) => {
    setFilter((prev) => ({ ...prev, name: searchTerm, username: searchTerm, page: 1 }));
  };

  const handleStatusChange = (selectedOption) => {
    setSelectedStatus(selectedOption);
    setFilter((prev) => ({
      ...prev,
      flag: selectedOption?.value || "",
      page: 1,
    }));
  };

  const clearFilters = () => {
    setSelectedStatus(null);
    setFilter(initialFilter);
  };

  const handlePageChange = (page) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const {
    isLoading: isAllUsersLoading,
    data: { data: allusers, page: currentPage, totalPages } = {},
  } = useAllUsers(filter);

  const { data: godownsData } = useAllGodowns({ noPagination: "true" });
  const godownMap = {};
  (godownsData?.data || []).forEach((g) => { godownMap[g.id] = g.name; });

  const getGodownLabel = (godownId) => {
    if (!godownId || godownId === 0) return "All Godowns";
    return godownMap[godownId] || `Godown #${godownId}`;
  };

  const getLevelLabel = (level) => {
    const labels = {
      1: "Level 1",
      2: "Level 2",
      3: "Level 3",
      4: "Level 4",
      5: "Level 5",
    };
    return labels[level] || `Level ${level}`;
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {deleteUserId && (
        <Modal open={!!deleteUserId} setOpen={(val) => !val && setDeleteUserId(null)}>
          <DeleteUser
            id={deleteUserId}
            onClose={() => setDeleteUserId(null)}
          />
        </Modal>
      )}

      {isSetModalOpen && (
        <SetUser open={isSetModalOpen} id={setUserId} onClose={closeSetModal} />
      )}

      {/* Header */}
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="font-semibold leading-6 text-3xl text-gray-900">
            Manage Users
          </h1>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            onClick={() => openSetModal()}
            className="block rounded-md bg-gray-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
          >
            Add User
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <InputSearch
          onSearch={handleSearch}
          label="Search Users"
          placeholder="Name or username"
        />

        <Select
          label="Status"
          options={statusOptions}
          value={selectedStatus}
          onChange={handleStatusChange}
          placeholder="Select status"
          isClearable
        />

        <div className="flex items-end">
          <button
            onClick={clearFilters}
            className="w-full rounded-md bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-300"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <Table>
              <THead>
                <TR>
                  <TH>Name</TH>
                  <TH>Username</TH>
                  <TH>Level</TH>
                  <TH>Godown</TH>
                  <TH>Status</TH>
                  <TH last>Actions</TH>
                </TR>
              </THead>
              <TBody>
                {isAllUsersLoading ? (
                  <LoadingRow colSpan={6} />
                ) : !allusers || allusers.length === 0 ? (
                  <EmptyRow colSpan={6} message="No users found" />
                ) : (
                  allusers.map((user) => (
                    <TR key={user.id}>
                      <TD>{user.name}</TD>
                      <TD>{user.username}</TD>
                      <TD>{getLevelLabel(user.level)}</TD>
                      <TD>{getGodownLabel(user.godown)}</TD>
                      <TD>
                        {user.flag === 1 ? (
                          <CheckIcon className="h-5 w-5 text-green-500" aria-hidden="true" />
                        ) : (
                          <XMarkIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                        )}
                      </TD>
                      <TD last>
                        <ActionButton
                          onClick={() => navigate(`/company/user-roles?userId=${user.id}&name=${encodeURIComponent(user.name)}`)}
                          variant="primary"
                          title="Permissions"
                        >
                          <ShieldCheckIcon className="h-4 w-4" />
                        </ActionButton>
                        <ActionButton
                          onClick={() => openSetModal(user.id)}
                          variant="secondary"
                          title="Edit"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </ActionButton>
                        <ActionButton
                          onClick={() => setDeleteUserId(user.id)}
                          variant="danger"
                          title="Delete"
                          className="mr-0"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </ActionButton>
                      </TD>
                    </TR>
                  ))
                )}
              </TBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            showPageJump={true}
          />
        </div>
      )}
    </div>
  );
}
